import React, { createContext, useContext, useState, useEffect } from 'react';
import { useBranding } from '../contexts/BrandingContext';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const branding = useBranding();  // Get current tenant branding

  // Get stored preference (light, dark, or system)
  const getStoredPreference = () => {
    // If this tenant doesn't allow theme toggle, force the default theme
    if (branding.allow_theme_toggle === false) {
      return branding.default_theme || 'dark';
    }

    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('theme');
      if (stored && ['light', 'dark', 'system'].includes(stored)) {
        return stored;
      }
    }
    return 'system'; // Default to system preference
  };

  // Get the actual theme to apply based on preference
  const getEffectiveTheme = (preference) => {
    // If this tenant doesn't allow theme toggle, force the default theme
    if (branding.allow_theme_toggle === false) {
      return branding.default_theme || 'dark';
    }

    if (preference === 'system') {
      if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark';
      }
      return 'light';
    }
    return preference;
  };

  const [themePreference, setThemePreference] = useState(() => getStoredPreference());
  const [effectiveTheme, setEffectiveTheme] = useState(() => getEffectiveTheme(getStoredPreference()));

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;

    if (effectiveTheme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [effectiveTheme]);

  // Update effective theme when preference changes
  useEffect(() => {
    // Don't save preference to localStorage if toggle is disabled
    if (branding.allow_theme_toggle !== false) {
      localStorage.setItem('theme', themePreference);
    }
    setEffectiveTheme(getEffectiveTheme(themePreference));
  }, [themePreference, branding.allow_theme_toggle]);

  // Re-evaluate theme when branding changes (tenant switch)
  useEffect(() => {
    const newPreference = getStoredPreference();
    setThemePreference(newPreference);
    setEffectiveTheme(getEffectiveTheme(newPreference));
  }, [branding.allow_theme_toggle]);

  // Listen for system preference changes when in 'system' mode
  useEffect(() => {
    // Skip if theme toggle is disabled for this tenant
    if (branding.allow_theme_toggle === false) return;
    if (themePreference !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e) => {
      setEffectiveTheme(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [themePreference, branding.allow_theme_toggle]);

  const setTheme = (newTheme) => {
    // Ignore theme changes if toggle is disabled for this tenant
    if (branding.allow_theme_toggle === false) return;
    if (['light', 'dark', 'system'].includes(newTheme)) {
      setThemePreference(newTheme);
    }
  };

  // Legacy toggle (cycles through: light -> dark -> system -> light)
  const toggleTheme = () => {
    // Ignore if toggle is disabled for this tenant
    if (branding.allow_theme_toggle === false) return;
    setThemePreference(prev => {
      if (prev === 'light') return 'dark';
      if (prev === 'dark') return 'system';
      return 'light';
    });
  };

  return (
    <ThemeContext.Provider value={{
      theme: effectiveTheme,           // The actual applied theme (light/dark)
      themePreference,                  // The user's preference (light/dark/system)
      setTheme,                         // Set specific theme
      toggleTheme,                      // Cycle through themes
      setLightTheme: () => setTheme('light'),
      setDarkTheme: () => setTheme('dark'),
      setSystemTheme: () => setTheme('system')
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
